import React, { useState } from "react";
import { HCaptchaWidget } from "../hcaptcha/hcaptcha";
import { useTranslation } from "../../hooks/translation";
import "./contact.css";

const MAIL_API = "https://lifter-studio-web-qm8o.vercel.app/api/send-email";

interface FormData {
  fullname: string;
  email: string;
  phone: string;
  message: string;
}

interface FormErrors {
  fullname?: string;
  email?: string;
  phone?: string;
  message?: string;
  captcha?: string;
}

interface ContactProps {
  lang: string;
}

export const Contact: React.FC<ContactProps> = ({ lang }) => {
  const { t } = useTranslation(lang);

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    fullname: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const validateField = (name: keyof FormData, value: string): string | undefined => {
    switch (name) {
      case "fullname":
        if (value.trim().length < 3) return t("contact.errors.name");
        break;
      case "email": {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return t("contact.errors.email");
        break;
      }
      case "phone": {
        if (value) {
          const phoneRegex = /^[0-9()+\-\s]+$/;
          if (!phoneRegex.test(value)) return t("contact.errors.phone");
        }
        break;
      }
      case "message":
        if (value.trim().length < 10) return t("contact.errors.message");
        break;
    }
    return undefined;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: validateField(name as keyof FormData, value) }));
  };

  const validateAll = (): boolean => {
    const newErrors: FormErrors = {};
    (Object.keys(formData) as Array<keyof FormData>).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    if (!captchaToken) newErrors.captcha = t("contact.errors.captcha");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedback(null);
    if (!validateAll()) return;
    setLoading(true);

    try {
      const res = await fetch(MAIL_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, captcha_token: captchaToken }),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error("Server error:", data);
        setFeedback({ type: "error", message: t("contact.feedback.error") });
        return;
      }

      setFormData({ fullname: "", email: "", phone: "", message: "" });
      setCaptchaToken(null);
      setErrors({});
      setFeedback({ type: "success", message: t("contact.feedback.success") });
    } catch (err) {
      console.error("Unexpected error:", err);
      setFeedback({ type: "error", message: t("contact.feedback.error") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="contact-section">
      <hr />
      <div className="container">
        <h1>{t("contact.title")}</h1>

        <form className="contact-form contact-card" onSubmit={handleSubmit} noValidate>
          <label>
            <input
              type="text"
              name="fullname"
              placeholder={t("contact.placeholders.name")}
              value={formData.fullname}
              onChange={handleChange}
              disabled={loading}
              className={errors.fullname ? "invalid" : ""}
            />
            {errors.fullname && <span className="error-message">{errors.fullname}</span>}
          </label>

          <label>
            <input
              type="email"
              name="email"
              placeholder={t("contact.placeholders.email")}
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              className={errors.email ? "invalid" : ""}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </label>

          <label>
            <input
              type="tel"
              name="phone"
              placeholder={t("contact.placeholders.phone")}
              value={formData.phone}
              onChange={handleChange}
              disabled={loading}
              className={errors.phone ? "invalid" : ""}
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </label>

          <label>
            <textarea
              name="message"
              placeholder={t("contact.placeholders.message")}
              value={formData.message}
              onChange={handleChange}
              disabled={loading}
              className={errors.message ? "invalid" : ""}
            ></textarea>
            {errors.message && <span className="error-message">{errors.message}</span>}
          </label>

          <HCaptchaWidget
            siteKey={process.env.REACT_APP_HCAPTCHA_SITEKEY!}
            onVerify={setCaptchaToken}
            onExpire={() => setCaptchaToken(null)}
            onError={() => setCaptchaToken(null)}
          />
          {errors.captcha && <span className="error-message">{errors.captcha}</span>}

          {feedback && (
            <div className={`thank-you-message ${feedback.type}`}>
              {feedback.message}
            </div>
          )}

          <button type="submit" disabled={loading}>
            {loading ? t("contact.buttons.wait") : t("contact.buttons.send")}
          </button>
        </form>
      </div>
      <div className="contact-section-tp-bg"></div>
    </section>
  );
};
